import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { 
  format, 
  startOfWeek, 
  startOfMonth,
  startOfYear,
  endOfWeek,
  endOfMonth,
  endOfYear,
  addDays, 
  addWeeks,
  addMonths,
  addYears,
  subWeeks,
  subMonths,
  subYears,
  isToday,
  isSameMonth,
  isSameYear,
  eachDayOfInterval,
  eachWeekOfInterval,
  getDaysInMonth
} from 'date-fns';
import StatsCard from './StatsCard';
import RecentActivity from './RecentActivity';
import QuickActions from './QuickActions';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiHome, FiUsers, FiCalendar, FiTrendingUp, FiClock, FiChevronLeft, FiChevronRight } = FiIcons;

const Dashboard = () => {
  const { user } = useAuth();
  const { properties, guestPages, dateFilter, isDateInFilter } = useData();
  const [scheduleView, setScheduleView] = useState('week'); // 'week', 'month', 'year'
  const [currentDate, setCurrentDate] = useState(new Date());

  // Filtered Data Calculations using context helper
  const filteredBookings = guestPages.filter(page => 
    isDateInFilter(page.checkInDate, dateFilter)
  );

  const filteredArrivals = guestPages.filter(page => 
    isDateInFilter(page.checkInDate, dateFilter)
  );

  // Calendar View Calculations
  const getWeekDays = () => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  };

  const getMonthDays = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const start = startOfWeek(monthStart, { weekStartsOn: 0 });
    const end = endOfWeek(monthEnd, { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  };

  const getYearMonths = () => {
    const yearStart = startOfYear(currentDate);
    return Array.from({ length: 12 }, (_, i) => addMonths(yearStart, i));
  };

  const navigateDate = (direction) => {
    if (direction === 'prev') {
      if (scheduleView === 'week') {
        setCurrentDate(subWeeks(currentDate, 1));
      } else if (scheduleView === 'month') {
        setCurrentDate(subMonths(currentDate, 1));
      } else if (scheduleView === 'year') {
        setCurrentDate(subYears(currentDate, 1));
      }
    } else {
      if (scheduleView === 'week') {
        setCurrentDate(addWeeks(currentDate, 1));
      } else if (scheduleView === 'month') {
        setCurrentDate(addMonths(currentDate, 1));
      } else if (scheduleView === 'year') {
        setCurrentDate(addYears(currentDate, 1));
      }
    }
  };

  const getDateRangeText = () => {
    if (scheduleView === 'week') {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
      return `${format(weekStart, 'MMM d')} - ${format(addDays(weekStart, 6), 'MMM d, yyyy')}`;
    } else if (scheduleView === 'month') {
      return format(currentDate, 'MMMM yyyy');
    } else if (scheduleView === 'year') {
      return format(currentDate, 'yyyy');
    }
    return '';
  };
  
  const getCheckInsForDay = (date) => {
    return guestPages.filter(page => {
      if (page.status !== 'active') return false;
      const checkInDate = new Date(page.checkInDate);
      const checkOutDate = new Date(page.checkOutDate);
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);
      
      return (checkInDate >= dayStart && checkInDate <= dayEnd) || 
             (checkOutDate >= dayStart && checkOutDate <= dayEnd);
    });
  };

  const getEventIcon = (page, date) => {
    const checkInDate = new Date(page.checkInDate);
    const checkOutDate = new Date(page.checkOutDate);
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);

    if (checkInDate >= dayStart && checkInDate <= dayEnd) {
      return { icon: FiUsers, color: 'green', type: 'Check-in' };
    }
    if (checkOutDate >= dayStart && checkOutDate <= dayEnd) {
      return { icon: FiCalendar, color: 'red', type: 'Check-out' };
    }
    return { icon: FiClock, color: 'gray', type: 'Activity' };
  };

  const getPropertyName = (propertyId) => {
    return properties.find(p => p.id === propertyId)?.name || 'Unknown Property';
  };

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard 
          title="Total Properties" 
          value={properties.length} 
          icon={FiHome} 
          color="blue" 
          subtitle="Portfolio size" 
          trend="" 
        />
        <StatsCard 
          title={`Arrivals (${dateFilter})`} 
          value={filteredArrivals.length} 
          icon={FiUsers} 
          color="green" 
          subtitle="Checking in" 
          trend="" 
        />
        <StatsCard 
          title={`Bookings (${dateFilter})`} 
          value={filteredBookings.length} 
          icon={FiCalendar} 
          color="purple" 
          subtitle="Total reservations" 
          trend="" 
        />
        <StatsCard 
          title="Total Guests" 
          value={guestPages.length} 
          icon={FiTrendingUp} 
          color="orange" 
          subtitle="All time" 
          trend="" 
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Activity & Schedule */}
        <div className="lg:col-span-2 space-y-6">
          <RecentActivity />
          
          {/* Schedule Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-sm p-6 border border-transparent"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-800">Schedule</h3>
              <div className="flex items-center space-x-3">
                {/* View Type Selector */}
                <div className="flex items-center bg-gray-100 rounded-lg p-1">
                  {['week', 'month', 'year'].map((view) => (
                    <button
                      key={view}
                      onClick={() => {
                        setScheduleView(view);
                        setCurrentDate(new Date());
                      }}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        scheduleView === view
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {view.charAt(0).toUpperCase() + view.slice(1)}
                    </button>
                  ))}
                </div>
                
                {/* Navigation */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => navigateDate('prev')}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiChevronLeft} className="text-gray-600" />
                  </button>
                  <button
                    onClick={() => setCurrentDate(new Date())}
                    className="text-xs font-medium text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full hover:bg-gray-200 transition-colors"
                  >
                    Today
                  </button>
                  <button
                    onClick={() => navigateDate('next')}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiChevronRight} className="text-gray-600" />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              <span className="text-sm font-medium text-gray-500">
                {getDateRangeText()}
              </span>
            </div>
            
            {/* Week View */}
            {scheduleView === 'week' && (
              <div className="space-y-4">
                {getWeekDays().map((day) => {
                  const dayEvents = getCheckInsForDay(day);
                  const eventCount = dayEvents.length;
                  
                  return (
                    <div key={day.toISOString()} className={`flex items-start p-3 rounded-2xl transition-colors ${isToday(day) ? 'bg-blue-50/50' : 'hover:bg-gray-50'}`}>
                      {/* Date Column */}
                      <div className="w-24 flex-shrink-0 pt-1">
                        <p className={`text-sm font-semibold ${isToday(day) ? 'text-blue-600' : 'text-gray-700'}`}>
                          {format(day, 'EEE')}
                        </p>
                        <p className="text-xs text-gray-400">{format(day, 'MMM d')}</p>
                      </div>
                      
                      {/* Events Column */}
                      <div className="flex-1 space-y-2">
                        {eventCount > 0 ? (
                          dayEvents.map((page, idx) => {
                            const info = getEventIcon(page, day);
                            return (
                              <div key={idx} className="flex items-center justify-between bg-white border border-gray-100 p-2.5 rounded-xl shadow-sm">
                                <div className="flex items-center space-x-3">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${info.color === 'green' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                                    <SafeIcon icon={info.icon} className="text-xs" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{page.guestName}</p>
                                    <div className="flex items-center mt-0.5">
                                      <span className="bg-gray-100 text-gray-600 text-[10px] px-1.5 py-0.5 rounded mr-2 font-medium">
                                        {getPropertyName(page.propertyId)}
                                      </span>
                                      <span className="text-xs text-gray-500">{info.type}</span>
                                    </div>
                                  </div>
                                </div>
                                <span className="text-xs font-semibold text-gray-400">
                                  {info.type === 'Check-in' ? '3:00 PM' : '11:00 AM'}
                                </span>
                              </div>
                            );
                          })
                        ) : (
                          <div className="h-8 flex items-center">
                            <div className="h-1.5 w-1.5 rounded-full bg-gray-200 mr-2"></div>
                            <p className="text-xs text-gray-400 italic">No events</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Month View */}
            {scheduleView === 'month' && (
              <div>
                {/* Calendar Grid Header */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <div key={day} className="text-center text-xs font-semibold text-gray-500 py-2">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {getMonthDays().map((day) => {
                    const dayEvents = getCheckInsForDay(day);
                    const isCurrentMonth = isSameMonth(day, currentDate);
                    const isTodayDate = isToday(day);
                    
                    return (
                      <div
                        key={day.toISOString()}
                        className={`min-h-[80px] p-2 border border-gray-100 rounded-lg ${
                          isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                        } ${isTodayDate ? 'ring-2 ring-blue-500' : ''}`}
                      >
                        <p className={`text-xs font-medium mb-1 ${isCurrentMonth ? 'text-gray-700' : 'text-gray-400'} ${isTodayDate ? 'text-blue-600' : ''}`}>
                          {format(day, 'd')}
                        </p>
                        <div className="space-y-1">
                          {dayEvents.slice(0, 2).map((page, idx) => {
                            const info = getEventIcon(page, day);
                            return (
                              <div
                                key={idx}
                                className={`text-[10px] px-1.5 py-0.5 rounded truncate ${
                                  info.color === 'green' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                }`}
                                title={`${page.guestName} - ${info.type}`}
                              >
                                {page.guestName}
                              </div>
                            );
                          })}
                          {dayEvents.length > 2 && (
                            <p className="text-[10px] text-gray-500">+{dayEvents.length - 2} more</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Year View */}
            {scheduleView === 'year' && (
              <div className="grid grid-cols-3 gap-4">
                {getYearMonths().map((month) => {
                  const monthEvents = guestPages.filter(page => {
                    if (page.status !== 'active') return false;
                    const checkInDate = new Date(page.checkInDate);
                    const checkOutDate = new Date(page.checkOutDate);
                    return isSameMonth(checkInDate, month) || isSameMonth(checkOutDate, month);
                  });
                  
                  return (
                    <div
                      key={month.toISOString()}
                      className="p-3 border border-gray-200 rounded-xl bg-gray-50"
                    >
                      <p className="text-sm font-semibold text-gray-700 mb-2">
                        {format(month, 'MMMM')}
                      </p>
                      <div className="space-y-1">
                        {monthEvents.length > 0 ? (
                          monthEvents.slice(0, 3).map((page, idx) => {
                            const checkInDate = new Date(page.checkInDate);
                            const isCheckIn = isSameMonth(checkInDate, month);
                            return (
                              <div
                                key={idx}
                                className={`text-xs px-2 py-1 rounded ${
                                  isCheckIn ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                }`}
                              >
                                {format(new Date(isCheckIn ? page.checkInDate : page.checkOutDate), 'MMM d')} - {page.guestName}
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-xs text-gray-400 italic">No events</p>
                        )}
                        {monthEvents.length > 3 && (
                          <p className="text-xs text-gray-500">+{monthEvents.length - 3} more</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </div>

        {/* Right Column: Quick Actions */}
        <div className="space-y-6">
          <QuickActions />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;